import React, { useState, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Grid, Row, Col, Text, Button } from 'rsuite';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import apiAccess from '../../../api/apiAccess';
import {
  CARD_ERROR,
  STRIPE_PAYMENT_INTENT_STATUSES,
  VALIDATION_ERROR,
} from './stripeConstants';
import { GENERIC_PAYMENT_ERROR } from '../constants';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmail } from '../../../store/slices/auth/authSelectors';
import {
  selectPaymentFees,
  selectPaymentID,
  selectPaymentSignatory,
  selectSelectedPaymentMethod,
} from '../../../store/slices/payment/paymentSelectors';
import Notice from '../../user-feedback/Notice';
import IconIndicator from '../../user-feedback/IconIndicator';
import { calculateTotalDue } from '../common/utils';
import { convertNumberToCurrency } from '../../../common/utils';

const StripeCreditCard = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const currentUserEmail = useSelector(selectCurrentUserEmail);
  const paymentID = useSelector(selectPaymentID);
  const signatoryName = useSelector(selectPaymentSignatory);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);
  const paymentFees = useSelector(selectPaymentFees);

  const [paymentInProgress, setPaymentsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const payButtonDisabled = useMemo(
    () => paymentInProgress || !stripe || !elements,
    [elements, paymentInProgress, stripe]
  );
  const totalDue = useMemo(
    () => calculateTotalDue(selectedPaymentMethod, paymentFees),
    [selectedPaymentMethod, paymentFees]
  );

  const paymentClick = async () => {
    if (payButtonDisabled) return;

    setPaymentsInProgress(true);
    setErrorMessage('');

    try {
      // Register payment in server, who responds with client secret so we can proceed with payment
      const { clientSecret } = await apiAccess.payByCreditCard(
        paymentID,
        signatoryName,
        currentUserEmail
      );

      // Make card payment directly with stripe. Requires client_secret from backend Stripe code
      // This object will have either  an error or paymentIntent property
      const result = await stripe.confirmCardPayment(clientSecret, {
        //this is differne for ach payments
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: { name: signatoryName },
        },
        receipt_email: currentUserEmail,
      });

      // Check for errors in stripe transaction
      if (result.error) {
        let errorMessage = GENERIC_PAYMENT_ERROR;
        /*
         * Errors can be card_error, api_error, idempotency_error, invalid_request_error.
         * Here we only display the nice `error.message` msg for card_error. Else we want
         * the user to see our own custom error message string
         */
        if (
          result.error.type === CARD_ERROR ||
          result.error.type === VALIDATION_ERROR
        ) {
          errorMessage = result.error.message;
        }

        setErrorMessage(errorMessage);
      } else if (
        result.paymentIntent.status === STRIPE_PAYMENT_INTENT_STATUSES.SUCCESS
      ) {
        onSuccess();
      }
    } catch (err) {
      setErrorMessage(GENERIC_PAYMENT_ERROR);
      Sentry.captureException(err);
    } finally {
      setPaymentsInProgress(false);
    }
  };

  return (
    <Grid fluid>
      <Row className="mb-16">
        <Col xs={24}>
          <Grid fluid>
            <Row>
              <Col xs={24}>
                <Text className="text-small mb-4">Card Number</Text>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <CardNumberElement
                  id="stripe-card-number"
                  className="stripe-element"
                  options={{ showIcon: true }}
                />
              </Col>
            </Row>
          </Grid>
        </Col>
      </Row>
      <Row className="mb-16">
        <Col
          xs={24}
          md={12}
          className="mb-16"
        >
          <Grid fluid>
            <Row>
              <Col xs={24}>
                <Text className="text-small mb-4">Expiry Date</Text>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <CardExpiryElement
                  className="stripe-element"
                  id="stripe-expiry"
                />
              </Col>
            </Row>
          </Grid>
        </Col>
        <Col
          xs={24}
          md={12}
        >
          <Grid fluid>
            <Row>
              <Col xs={24}>
                <Text className="text-small mb-4">CVC</Text>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <CardCvcElement
                  className="stripe-element"
                  id="stripe-cvc"
                />
              </Col>
            </Row>
          </Grid>
        </Col>
      </Row>
      {errorMessage && (
        <Row className="mb-16">
          <Col xs={24}>
            <Notice message={errorMessage} />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={24}>
          <Grid fluid>
            {paymentInProgress && (
              <Row className="mb-16">
                <Col xs={24}>
                  <IconIndicator />
                </Col>
              </Row>
            )}
            <Row>
              <Col xs={24}>
                <Button
                  className="btn-payment btn-soft-blue"
                  onClick={() => {
                    paymentClick();
                  }}
                  disabled={payButtonDisabled}
                >
                  Pay {convertNumberToCurrency(totalDue)}
                </Button>
              </Col>
            </Row>
          </Grid>
        </Col>
      </Row>
    </Grid>
  );
};

export default StripeCreditCard;
